import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "components/Common/Breadcrumb";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { getContactLists } from "store/contacts-lists/actions";
import { ToastContainer } from "react-toastify";
import Swal from 'sweetalert2';
import {
  deleteContactList,
  exportContactList,
  apiErrorMessage,
  startContactListImport,
  setImportAddExisting,
  commitContactImport,
  waitForContactImport,
  cancelContactImport,
} from "../../../helpers/api_helper";
import { getContactSummary } from "../../../helpers/contact_list_helper";

// ─── Import-from-file dialogs ───
const escapeHtml = (v) =>
  String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const plural = (n, one, many) => `${Number(n).toLocaleString()} ${n === 1 ? one : many}`;

const showImportLoading = (title, text = "") =>
  Swal.fire({
    title,
    text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });

// Some rows are already contacts in the tenant — ask whether to add them to the new list.
const askAboutExistingContacts = (summary, listName) => {
  const total = Number(summary.total || 0);
  const existing = Number(summary.existingInTenant || 0);
  const alreadyInList = Number(summary.existingAlreadyInList || 0);
  const notAccessible = Number(summary.existingNotAccessible || 0);
  const notes = [];
  if (alreadyInList > 0) {
    notes.push(`${plural(alreadyInList, "contact is", "contacts are")} already on this list.`);
  }
  if (notAccessible > 0) {
    notes.push(`${plural(notAccessible, "contact is", "contacts are")} outside your access and will be skipped.`);
  }
  return Swal.fire({
    title: "Some contacts already exist",
    html:
      `<p>${existing.toLocaleString()} of ${plural(total, "contact", "contacts")} in this file ${existing === 1 ? "is" : "are"} already in your contacts. ` +
      `Add them to the new list "<strong>${escapeHtml(listName)}</strong>"?</p>` +
      notes.map((n) => `<p class="text-muted mb-1 font-size-13">${n}</p>`).join(""),
    icon: "question",
    showDenyButton: true,
    showCancelButton: true,
    confirmButtonText: "Add them to the list",
    denyButtonText: "Skip them",
    cancelButtonText: "Cancel",
    allowOutsideClick: false,
  });
};

// Why a file produced nothing to import.
const nothingToImportText = (summary) => {
  const reasons = [];
  const invalid = Number(summary.invalid || 0);
  const blanks = Number(summary.blanks || 0);
  const incomplete = Number(summary.incomplete || 0);
  const duplicates = Number(summary.duplicates || 0);
  if (invalid) reasons.push(`${plural(invalid, "row has an invalid", "rows have invalid")} phone number${invalid === 1 ? "" : "s"}`);
  if (blanks) reasons.push(`${plural(blanks, "row has", "rows have")} no phone number`);
  if (incomplete) reasons.push(`${plural(incomplete, "row is", "rows are")} missing required details`);
  if (duplicates) reasons.push(`${plural(duplicates, "row is a duplicate", "rows are duplicates")}`);
  const why = reasons.length ? ` ${reasons.join("; ")}.` : "";
  return `None of the ${plural(Number(summary.total || 0), "row", "rows")} in this file can be imported.${why} Please check the file and try again.`;
};

// `result` is the finished session when the job completed in time: its real
// counts win over the preview's.
const importSuccessHtml = (summary, listName, addExisting, result) => {
  const done = result?.status === "IMPORTED";
  const imported = done ? Number(result.validRecords || 0) : Number(summary.wouldImport || 0);
  const linked = done
    ? Number(result.existingLinked || 0)
    : addExisting ? Number(summary.existingLinkable || 0) : 0;
  const skipped = addExisting ? 0 : Number(summary.existingInTenant || 0);
  const parts = [];
  if (imported > 0) parts.push(`${plural(imported, "new contact", "new contacts")} imported`);
  if (linked > 0) parts.push(`${plural(linked, "existing contact", "existing contacts")} added`);
  let html = `<p>${parts.join(", ")} to "<strong>${escapeHtml(listName)}</strong>".</p>`;
  if (skipped > 0) {
    html += `<p class="text-muted mb-1 font-size-13">${plural(skipped, "existing contact was", "existing contacts were")} skipped.</p>`;
  }
  if (!done) {
    html += `<p class="text-muted mb-0 font-size-13">The import is still running in the background — the list appears once it finishes.</p>`;
  }
  return html;
};

const ContactsList = () => {
  // Meta title
  document.title = "Contact List | Contact Lists";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [pagination, setPagination] = useState({
    pageSize: 10,
    page: 0,
    sortBy: 'id',
    ascendingSort: false,
  });

  const [modal, setModal] = useState(false);
  const [listName, setListName] = useState("");
  const [file, setFile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  useEffect(() => {
    getContactSummary().then(setSummary).catch(() => setSummary(null));
  }, []);

  // Debounced server-side list search.
  useEffect(() => {
    const h = setTimeout(() => {
      setPagination((p) => ({ ...p, page: 0 }));
      setActiveSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(h);
  }, [searchInput]);

  const toggleModal = () => setModal(!modal);

  const handleNewAddressBook = () => {
    toggleModal();
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    const name = listName.trim();
    if (!file || !name) {
      Swal.fire("Error!", "Please provide both a list name and a file.", "error");
      return;
    }

    setModal(false);
    showImportLoading("Uploading contact list…", "Reading the file — large files can take a minute.");

    let ctx = null;
    try {
      ctx = await startContactListImport(file, name);
      let summary = ctx.summary;
      let addExisting = false;

      const existing = Number(summary.existingInTenant || 0);
      const wouldImport = Number(summary.wouldImport || 0);

      if (existing > 0) {
        const choice = await askAboutExistingContacts(summary, name);
        if (choice.isConfirmed) {
          showImportLoading("Preparing import…");
          summary = await setImportAddExisting(ctx, true);
          addExisting = true;
          if (Number(summary.wouldImport || 0) === 0 && Number(summary.existingLinkable || 0) === 0) {
            await cancelContactImport(ctx.sessionId);
            Swal.fire("Nothing to import", nothingToImportText(summary), "info");
            return;
          }
        } else if (choice.isDenied) {
          if (wouldImport === 0) {
            await cancelContactImport(ctx.sessionId);
            Swal.fire(
              "Nothing to import",
              `All the contacts in this file already exist, so skipping them leaves nothing to import. The list "${name}" was not created.`,
              "info"
            );
            return;
          }
        } else {
          await cancelContactImport(ctx.sessionId);
          return;
        }
      } else if (wouldImport === 0) {
        await cancelContactImport(ctx.sessionId);
        Swal.fire("Nothing to import", nothingToImportText(summary), "warning");
        return;
      }

      showImportLoading("Importing contacts…");
      await commitContactImport(ctx.sessionId);
      const result = await waitForContactImport(ctx.sessionId);

      if (result?.status === "FAILED" || result?.status === "CANCELLED") {
        Swal.fire(
          "Import failed",
          `The contacts could not be imported into "${name}". Please try again — if it keeps failing, contact support.`,
          "error"
        );
        return;
      }
      Swal.fire({
        title: result?.status === "IMPORTED" ? "Success!" : "Import started",
        html: importSuccessHtml(summary, name, addExisting, result),
        icon: result?.status === "IMPORTED" ? "success" : "info",
      });
      setListName("");
      setFile(null);

      // Refresh the contact list after successful upload
      dispatch(getContactLists({ ...pagination, search: activeSearch }));
      getContactSummary().then(setSummary).catch(() => {});
    } catch (error) {
      console.error("Error uploading contact list:", error);
      Swal.fire(
        "Error!",
        error?.userMessage || apiErrorMessage(error, "Failed to upload the contact list."),
        "error"
      );
    }
  };

  const handleExportClick = async () => {
    try {
      await exportContactList();
      Swal.fire("Success!", "Contact list exported successfully.", "success");
    } catch (error) {
      Swal.fire("Error!", "Failed to export the contact list.", "error");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "List Name",
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (cell) => (
          <h5 className="font-size-14 mb-1">
            <Link to="#" className="text-dark">
              {cell.getValue()}
            </Link>
          </h5>
        )
      },
      {
        header: "Number of Contacts",
        accessorKey: "numberOfContacts",
        enableColumnFilter: false,
        cell: (info) => {
          const v = info.getValue();
          return v == null || v === "" ? "—" : Number(v).toLocaleString();
        },
      },
      {
        header: "Create Date",
        accessorKey: "created_at",
        enableColumnFilter: false,
        //   cell: (cellProps) => {
        //     const dateValue = cellProps.getValue();
        //     const formattedDate = dateValue ? moment(dateValue).format('YYYY MMM DD h:mm A') : '';
        //     return <span>{formattedDate}</span>;
        //   },
        // },
        cell: (cell) => {
          const dateValue = cell.getValue();
          const date = new Date(dateValue);
          if (isNaN(date)) {
            return <p className="font-size-14 mb-1">Invalid date</p>;
          }
          const formattedDate = date
            ? moment(date).format('ddd DD MMM YYYY')
            : '';
          return (
            <p className="font-size-14 mb-1">
              {formattedDate}
            </p>
          );
        }
      },
      {
        header: "Actions",
        enableColumnFilter: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-success"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/contact-list-detail/${cellProps.row.original.id}`);
              }}
            >
              <i className="mdi mdi-eye font-size-18" />
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDeleteContactList(cellProps.row.original.id);
              }}
            >
              <i className="mdi mdi-delete font-size-18" />
            </Link>
          </div>
        )
      },
    ],
    [navigate]
  );

  const sampleData = [
    { id: 1, name: "Customer List A", numberOfContacts: 25, createDate: "2023-12-15" },
    { id: 2, name: "Vendor List B", numberOfContacts: 12, createDate: "2024-01-01" },
    { id: 3, name: "Employee List C", numberOfContacts: 50, createDate: "2023-11-20" },
  ];

  const handleCreateUserClick = () => {
    console.log("Create button clicked!");
  };

  const handleDeleteContactList = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#800080',
      confirmButtonText: '<i class="mdi mdi-delete"></i> Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await deleteContactList(id);
        Swal.fire('Deleted!', 'The contact list has been deleted.', 'success');
        dispatch(getContactLists({ ...pagination, search: activeSearch })); // Refresh the list
      } catch (error) {
        Swal.fire('Error!', 'Failed to delete the contact list. Please try again.', 'error');
      }
    }
  };

  useEffect(() => {
    dispatch(getContactLists({ ...pagination, search: activeSearch }))
  }, [pagination, activeSearch]);

  const ContactListProperties = createSelector(
    (state) => state.ContactList,
    (contactList) => ({
      error: contactList.error,
      contactLists: contactList.contactLists,
      total: contactList.contactListsTotal,
      totalPages: contactList.contactListsTotalPages,
    })
  );

  const {
    error,
    contactLists,
    total,
    totalPages,
  } = useSelector(ContactListProperties);

  const onChangePagination = (
    { page, limit }, filters, sorter) => {
    setPagination({ ...pagination, page: page ?? pagination.page, pageSize: limit ?? pagination.pageSize })
  }

  const stats = [
    { title: "Total Lists", icon: "bx-list-ul", value: (total || 0).toLocaleString() },
    { title: "Total Contacts", icon: "bx-group", value: (summary?.totalContacts || 0).toLocaleString() },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Contacts" breadcrumbItem="Lists" />
          <Row>
            {stats.map((s, i) => (
              <Col md="6" key={i}>
                <Card className="mini-stats-wid">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-2">{s.title}</p>
                        <h4 className="mb-0">{s.value}</h4>
                      </div>
                      <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                        <span className="avatar-title rounded-circle bg-primary">
                          <i className={"bx " + s.icon + " font-size-24"}></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">Lists</h4>
                  <Row className="mb-3 align-items-center">
                    <Col md={6}>
                      <div className="d-flex align-items-center">
                        <Input
                          style={{ maxWidth: 370 }}
                          placeholder="Search lists by name…"
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                        />
                        {activeSearch && (
                          <small className="text-muted ms-3">{total.toLocaleString()} result(s) for “{activeSearch}”</small>
                        )}
                      </div>
                    </Col>
                    <Col md={6} className="text-md-end mt-2 mt-md-0">
                      <Button outline color="primary" className="me-2" onClick={handleExportClick}>
                        <i className="mdi mdi-download"></i> Export Data
                      </Button>
                      <Button color="primary" onClick={handleNewAddressBook}>
                        <i className="mdi mdi-plus"></i> New Address Book
                      </Button>
                    </Col>
                  </Row>
                  <TableContainer
                    columns={columns}
                    data={contactLists || []}
                    isGlobalFilter={false}
                    isPagination={true}
                    isCustomPageSize={true}
                    pageSizeAtBottom={true}
                    paginationData={pagination}
                    onChangePagination={onChangePagination}
                    manualPagination={true}
                    pageCount={totalPages}
                    totalRows={total}
                    onRowClick={(row) => navigate(`/contact-list-detail/${row.id}`)}
                    tableClass="align-middle table-nowrap table-hover dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    pagination="pagination"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>New Address Book</ModalHeader>
        <Form onSubmit={handleFormSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="listName">List Name</Label>
              <Input
                type="text"
                id="listName"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                placeholder="Enter list name"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label for="fileUpload">Upload Excel File</Label>
              <Input
                type="file"
                id="fileUpload"
                onChange={handleFileChange}
                accept=".xlsx, .xls, .csv"
                required
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              Save
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default ContactsList;
